import { SavedSearch, Location } from '../models';
import { v4 as uuidv4 } from 'uuid';
// Removed apiClient import - no longer needed here

// Local storage keys
const KEYS = {
  SAVED_SEARCHES: 'tideAnalyzer.savedSearches',
  PREFERENCES: 'tideAnalyzer.preferences',
  DISCLAIMER_ACKNOWLEDGED: 'tideAnalyzer.disclaimerAcknowledged',
  SELECTED_OPPORTUNITIES: 'tideAnalyzer.selectedOpportunities',
};

/**
 * Manager for browser local storage operations - Focused on basic CRUD.
 */
class LocalStorageManager {
  // Removed hasRunInitialRepair flag

  /**
   * Saves a new search to local storage
   * Saves a new search (with nested parameters) to local storage
   */
  // Input type now expects the full SavedSearch structure (minus id/createdAt)
  saveSearch(search: Omit<SavedSearch, 'id' | 'createdAt'>): SavedSearch {
    try {
      // Validate the nested parameters object exists
      if (!search.parameters) {
        throw new Error('Cannot save search: Missing parameters object');
      }
      // Validate locationId within parameters
      if (!search.parameters.locationId) {
          throw new Error('Cannot save search: Missing locationId in parameters');
      }
      // Optional: Add validation for dateOfInterest and services if needed

      // Get existing searches
      const searches = this.getSavedSearches();

      // Ensure autoSave defaults correctly and add schema version
      const newSearch: SavedSearch & { _schemaVersion: number } = { // Add type intersection
        id: uuidv4(),
        name: search.name,
        parameters: search.parameters, // Store the nested parameters
        createdAt: new Date().toISOString(),
        autoSave: search.autoSave !== undefined ? search.autoSave : true,
        _schemaVersion: 2 // Add schema version (LATEST_SCHEMA_VERSION)
      };

      // Add the search
      searches[newSearch.id] = newSearch;
      
      // Save to local storage
      localStorage.setItem(KEYS.SAVED_SEARCHES, JSON.stringify(searches));
      
      return newSearch;
    } catch (error) {
      console.error('Error saving search:', error);
      throw error;
    }
  }

  /**
   * Updates an existing search in local storage
   * Updates an existing search (with nested parameters) in local storage
   */
  // Input type now expects the full SavedSearch structure (minus id/createdAt)
  updateSearch(searchId: string, search: Omit<SavedSearch, 'id' | 'createdAt'>): SavedSearch | null {
    try {
      // Validate the nested parameters object exists
      if (!search.parameters) {
        throw new Error('Cannot update search: Missing parameters object');
      }
      // Validate locationId within parameters
      if (!search.parameters.locationId) {
          throw new Error('Cannot update search: Missing locationId in parameters');
      }
      // Optional: Add validation for dateOfInterest and services if needed

      // Get existing searches
      const searches = this.getSavedSearches();
      
      // Check if the search exists
      const existingSearch = searches[searchId];
      if (!existingSearch) {
        console.error('Search not found for update:', searchId);
        return null;
      }

      // Update the search, preserving id/createdAt, using new parameters, and ensure schema version
      const updatedSearch: SavedSearch & { _schemaVersion: number } = { // Add type intersection
        id: searchId,
        name: search.name,
        parameters: search.parameters, // Use the new parameters object
        createdAt: existingSearch.createdAt, // Preserve original creation timestamp
        autoSave: search.autoSave !== undefined ? search.autoSave : existingSearch.autoSave, // Preserve or update autoSave
        _schemaVersion: 2 // Ensure schema version is present/updated (LATEST_SCHEMA_VERSION)
      };

      // Update in storage
      searches[searchId] = updatedSearch;
      localStorage.setItem(KEYS.SAVED_SEARCHES, JSON.stringify(searches));

      return updatedSearch;
    } catch (error) {
      console.error('Error updating search:', error);
      throw error;
    }
  }

   /**
   * Gets all saved searches from local storage
   * Gets all saved searches from local storage. Assumes new format with nested parameters.
   */
  getSavedSearches(): Record<string, SavedSearch> {
    try {
      const searchesJson = localStorage.getItem(KEYS.SAVED_SEARCHES);
      // Basic check if data exists
      if (!searchesJson) {
        return {};
      }
      // TODO: Add more robust parsing and validation if needed
      const searches = JSON.parse(searchesJson);
      return searches;
    } catch (error) {
      console.error('Error getting saved searches:', error);
      return {};
    }
  }

  /**
   * Deletes a saved search from local storage
   */
  deleteSavedSearch(searchId: string): void {
    try {
      // Get existing searches
      const searches = this.getSavedSearches();
      
      // Delete the search
      delete searches[searchId];
      
      // Save to local storage
      localStorage.setItem(KEYS.SAVED_SEARCHES, JSON.stringify(searches));
    } catch (error) {
      console.error('Error deleting saved search:', error);
      throw error;
    }
  }

  /**
   * Saves user preferences to local storage
   */
  savePreferences(preferences: Record<string, any>): void {
    try {
      localStorage.setItem(KEYS.PREFERENCES, JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving preferences:', error);
      throw error;
    }
  }

  /**
   * Gets user preferences from local storage
   */
  getPreferences(): Record<string, any> {
    try {
      const preferencesJson = localStorage.getItem(KEYS.PREFERENCES);
      return preferencesJson ? JSON.parse(preferencesJson) : {};
    } catch (error) {
      console.error('Error getting preferences:', error);
      return {};
    }
  }

  // Removed validateAndRepairSavedSearchesAsync method - moved to useSearchManagement hook

  /**
   * Saves the entire searches object (Record<string, SavedSearch>) to local storage.
   * Used after validation/repair in the hook.
   */
  saveRawSearches(searches: Record<string, SavedSearch>): void {
    try {
      localStorage.setItem(KEYS.SAVED_SEARCHES, JSON.stringify(searches));
    } catch (error) {
      console.error('Error saving raw searches object:', error);
      throw error; // Re-throw to allow calling function to handle
    }
  }

  /**
   * Toggles auto-save for a saved search
   */
  toggleAutoSave(searchId: string): SavedSearch | null {
    try {
      // Get existing searches
      const searches = this.getSavedSearches();
      
      // Check if the search exists
      if (!searches[searchId]) {
        console.error('Search not found:', searchId);
        return null;
      }
      
      // Toggle the autoSave property
      const search = searches[searchId];
      search.autoSave = !search.autoSave;
      
      // Update in storage
      searches[searchId] = search;
      localStorage.setItem(KEYS.SAVED_SEARCHES, JSON.stringify(searches));
      
      return search;
    } catch (error) {
      console.error('Error toggling auto-save for search:', error);
      throw error;
    }
  }

  /**
   * Gets selected service opportunities for a specific date
   * @param date ISO date format (YYYY-MM-DD)
   * @returns Array of opportunity IDs selected for that date
   */
  getSelectedOpportunities(date: string): string[] {
    try {
      const selectedOpportunitiesJson = localStorage.getItem(KEYS.SELECTED_OPPORTUNITIES);
      const selectedOpportunities = selectedOpportunitiesJson ? JSON.parse(selectedOpportunitiesJson) : {};
      return selectedOpportunities[date] || [];
    } catch (error) {
      console.error('Error getting selected opportunities:', error);
      return [];
    }
  }

  /**
   * Saves selected service opportunities for a specific date
   * @param date ISO date format (YYYY-MM-DD)
   * @param opportunityIds Array of opportunity IDs selected for that date
   */
  saveSelectedOpportunities(date: string, opportunityIds: string[]): void {
    try {
      const selectedOpportunitiesJson = localStorage.getItem(KEYS.SELECTED_OPPORTUNITIES);
      const selectedOpportunities = selectedOpportunitiesJson ? JSON.parse(selectedOpportunitiesJson) : {};
      selectedOpportunities[date] = opportunityIds;
      localStorage.setItem(KEYS.SELECTED_OPPORTUNITIES, JSON.stringify(selectedOpportunities));
    } catch (error) {
      console.error('Error saving selected opportunities:', error);
    }
  }
}

// Create a singleton instance of the local storage manager
export const localStorageManager = new LocalStorageManager();
